import type { ContractItemResponse, CreateContractItemPayload } from './types';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { zodContractItemResponseSchema } from './validations';

/**
 * Creates a new line item for a contract.
 *
 * @param contractId - The UUID of the contract to add the item to.
 * @param payload - The contract item data to create.
 * @returns The created contract item with its generated ID and metadata.
 * @throws Error if the API request fails. Validation errors are logged but do not throw.
 * @see https://app.dev.vestigas.com/redoc#tag/Contracts/operation/create_contract_item_contracts__contract_id__items_post
 */
export const createContractItem = async (
  contractId: UUID,
  payload: CreateContractItemPayload,
) => {
  const response = await vestigasApi
    .post(ENDPOINT.CONTRACT_ITEM.CREATE(contractId), {
      json: payload,
    })
    .json<ContractItemResponse>();

  // Validate the response with Zod
  const validatedResponse = zodContractItemResponseSchema.safeParse(response);
  if (!validatedResponse.success && validatedResponse.error.issues.length) {
    log.error({
      message: 'Create contract item API response has validation errors',
      error: {
        errors: validatedResponse.error.issues,
        errorMessage: validatedResponse.error.message,
        response,
      },
    });
  }

  return response;
};
